// src/components/Sidebar/useVirtualList.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Minimal fixed-row-height virtualizer with no external dependency.
 * Only the rows within the visible viewport (+ overscan) are mounted,
 * so the DOM stays small and scroll stays smooth even with thousands
 * of projects. The scroll container itself is a normal element with
 * native overflow — wheel/touchpad/keyboard/touch scrolling all work
 * exactly as they would for any other scrollable div.
 */
export function useVirtualList({ count, itemHeight, overscan = 6 }) {
  const containerRef = useRef(null);
  const [range, setRange] = useState({ start: 0, end: Math.min(count, 30) });

  const recalc = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const { scrollTop, clientHeight } = el;
    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const visibleRows = Math.ceil(clientHeight / itemHeight) + overscan * 2;
    const end = Math.min(count, start + visibleRows);

    setRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, [count, itemHeight, overscan]);

  useEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let raf = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        recalc();
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recalc) : null;
    ro?.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [recalc]);

  return { containerRef, range, totalHeight: count * itemHeight };
}
